import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import PiezometryAction from '../../../../../piezometry/actions/PiezometryAction'
import { setConfirmationModal } from '../../../../../utils/FormUtils'
import { getLabel } from '../../../../../utils/StoreUtils'
import { getI18nTitleDataLength } from '../../../../../utils/StringUtil'
import CentralAction from '../../actions/CentralAction'
import CentralDto from '../../dto/CentralDto'
import DtoCentralChannel from '../../dto/DtoCentralChannel'
import { CheckBox, PortableWifiOff, WifiTethering } from '@mui/icons-material'
import { darkBlue } from 'utils/constants/ColorTheme'
import { CardTable } from 'components/datatable/NewTable'
import { Button, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import NumberField from 'components/forms/NumberField'
import Textarea from 'components/forms/Textarea'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import Checkbox from 'components/forms/Checkbox'

const ChannelPanel = ({ central, active, centralChannels, refreshDataFunction }) => {
    const [choosenChannel, setChoosenChannel] = useState({ centralId: central.id })
    const [openModal, setOpenModal] = useState(false)
    const [isNew, setIsNew] = useState(true)

    const {
        piezometryDataTypes,
    } = useSelector(store => ({
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        if (!piezometryDataTypes.length) {
            dispatch(PiezometryAction.fetchPiezometryDataTypes())
        }
    }, [])

    const dataTypes = [{ id: 0, label: i18n.depth }, ...piezometryDataTypes]

    const getRemoteTranmissionIcon = (teletransmission) => {
        if (teletransmission === '1') {
            return <WifiTethering />
        }
        return <PortableWifiOff />
    }

    const getAcquisitionIcon = (register) => {
        if (register === '1') {
            return <CheckBox />
        }
        return undefined
    }

    const datas = useMemo(() => {
        if (centralChannels.length) {
            return centralChannels.map(o => {
                const reasignValue = {
                    ...o,
                    remoteTransmissionIcon: getRemoteTranmissionIcon(o.remoteTransmission),
                    acquisition: getAcquisitionIcon(o.register),
                    dataTypeLabel: getLabel(dataTypes, o.dataType, null, 'id'),
                }
                return reasignValue
            })
        }
        return []
    }, [centralChannels, active, dataTypes])

    const modalContent = useMemo(() => (
        <Stack spacing={1}>
            <Grid container spacing={2}>
                <Grid size={4}>
                    <NumberField title={i18n.channel} value={choosenChannel.channelId}
                        onChange={v => setChoosenChannel({ ...choosenChannel, channelId: v })} disabled={!isNew}
                    />
                </Grid>
                <Grid size={4}>
                    <Input title={i18n.name} value={choosenChannel.name} onChange={v => setChoosenChannel({ ...choosenChannel, name: v })} />
                </Grid>
                <Grid size={4}>
                    <Select value={choosenChannel.dataType} options={dataTypes} label={i18n.dataType}
                        onChange={v => setChoosenChannel({ ...choosenChannel, dataType: v })} integerValue
                    />
                </Grid>
            </Grid>
            <Textarea title={i18n.comment} value={choosenChannel.comment}
                onChange={v => setChoosenChannel({ ...choosenChannel, comment: v })}
            />
            <Grid container spacing={2}>
                <Grid size={6}>
                    <NumberField title={i18n.periodicity} value={choosenChannel.periodicity}
                        onChange={v => setChoosenChannel({ ...choosenChannel, periodicity: v })}
                    />
                </Grid>
                <Grid size={6}>
                    <Input title={i18n.assignment} value={choosenChannel.assignment}
                        onChange={v => setChoosenChannel({ ...choosenChannel, assignment: v })}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} alignItems={'center'}>
                <Grid size={6}>
                    <Checkbox checked={choosenChannel.remoteTransmission === '1'} label={i18n.remoteTransmission}
                        onChange={v => setChoosenChannel({ ...choosenChannel, remoteTransmission: v ? '1' : undefined })}
                    />
                </Grid>
                <Grid size={6}>
                    <Checkbox checked={choosenChannel.register === '1'} label={i18n.acquisition}
                        onChange={v => setChoosenChannel({ ...choosenChannel, register: v ? '1' : undefined })}
                    />
                </Grid>
            </Grid>
        </Stack>
    ), [choosenChannel])

    const actions = [
        {
            icon: 'note_add',
            color: darkBlue,
            onClick: () => setOpenModal(true),
            displayed: active,
        },
    ]
    const lineActions = [
        {
            icon: 'edit',
            onClick: (selectedChannel) => {
                setChoosenChannel(new DtoCentralChannel(selectedChannel))
                setIsNew(false)
                setOpenModal(true)
            },
            displayed: active,
        },
        {
            icon: 'delete',
            onClick: (selectedChannel) => setConfirmationModal(() => dispatch(CentralAction.updateCentralChannel(central.id, new DtoCentralChannel(selectedChannel), 'DELETE', () => refreshDataFunction()))),
            displayed: active,
        },
    ]
    return (
        <Stack>
            <CardTable
                title={getI18nTitleDataLength(i18n.path, i18n.paths, centralChannels.length)}
                actions={actions}
                lineActions={lineActions}
                rows={datas}
                headers={['channelId', { value: i18n.dataType, key: 'dataTypeLabel' }, 'assignment', 'comment', { value: i18n.remoteTransmission, key: 'remoteTransmissionIcon' }, 'acquisition']}
            />
            <Dialog
                fullWidth
                maxWidth='sm'
                open={openModal}
                onClose={() => {
                    setOpenModal(false)
                    setIsNew(true)
                    setChoosenChannel({ centralId: central.id })
                }}
            >
                <DialogContent>
                    {modalContent}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant={'outlined'}
                        onClick={() => {
                            setOpenModal(false)
                            setIsNew(true)
                            setChoosenChannel({ centralId: central.id })
                        }}
                    >{i18n.cancel}</Button>
                    <Button
                        variant={'contained'}
                        onClick={() => {
                            dispatch(CentralAction.updateCentralChannel(central.id, new DtoCentralChannel(choosenChannel), isNew ? 'POST' : 'PUT')).then(() => {
                                setOpenModal(false)
                                setIsNew(true)
                                setChoosenChannel({ centralId: central.id })
                                refreshDataFunction()
                            })
                        }}
                    >{i18n.register}</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

ChannelPanel.propTypes = {
    central: PropTypes.instanceOf(CentralDto),
    centralChannels: PropTypes.arrayOf(PropTypes.instanceOf(DtoCentralChannel)),
    active: PropTypes.bool,
    refreshDataFunction: PropTypes.func,
}

export default ChannelPanel